// The signed note, beside the claim it is being coded from.
//
// Mounted by the claim editor (features/claims/ClaimEditor.js) as one line. It
// owns nothing on the claim: the only write it makes is the per-claim "note
// reviewed" flag, which is the biller saying they have read it. Codes reach a
// claim through the editor's own edit path and a person's click, never from
// here — see SuggestPanel (phase 2), which proposes and does not apply.
//
// Gates, both of which must pass before a single character of a note is fetched:
//   * `readOnly` — the client role is the viewer role, and never sees a note;
//   * `features.note_viewer` — off until the practice has notes at all.
// The server enforces both again (PHI audit, feature gate); this stops the
// request being made, it is not the boundary.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchClaimNotes, setNoteReviewed } from "./api";
import { assessmentIndex, findMatches, findQuote, segmentNote } from "./noteText";
import NoteBody from "./NoteBody";
import SuggestPanel from "./SuggestPanel";
import { takeNotesTarget } from "./notesTarget";
import "./Notes.css";

function formatDate(iso) {
  if (!iso) return null;
  const [date] = String(iso).split("T");
  const [y, m, d] = date.split("-");
  if (!y || !m || !d) return String(iso);
  return `${m}/${d}/${y}`;
}

function formatWhen(iso) {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return String(iso);
  return parsed.toLocaleString();
}

function scrollTo(element) {
  // jsdom has no layout, and neither does a node that never mounted.
  if (element && typeof element.scrollIntoView === "function") {
    element.scrollIntoView({ block: "center" });
  }
}

export default function NotesPane({
  practiceId,
  claimId,
  features = {},
  readOnly = false,
  // Told when the review flag moves, so a host can refresh anything derived from
  // it. The claim editor deliberately does NOT pass it: its only refresh is a
  // full reload that blanks the page to "Loading claim…", which would unmount
  // this pane, re-read the note (a second PHI read and a second audit row) and
  // throw away the search and the open tab — to refresh a readiness row. Until
  // the editor has a refresh that does not tear itself down, the checklist
  // catches up on the next load and the pane says so.
  onReviewedChange,
  // A verbatim quote to highlight in the note. The Suggest panel below sets this
  // itself when a line is selected; the prop is an override for a host that
  // wants to point at a sentence for some other reason.
  quote = null,
  // Told which note is on screen, for a host that needs to know.
  onActiveNoteChange,
  // False when the payer has already seen the claim. Forwarded to the Suggest
  // panel, which then offers no Accept at all.
  canEdit = true,
  // Read-only, forwarded to the Suggest panel so it can say why a code cannot
  // be accepted. Nothing in this feature writes to the claim.
  claimIcds,
  claimLines,
  // The editor's own way of putting a code on the claim, handed down to the
  // Suggest panel. THESE ARE THE ONLY PATH from a suggestion to a claim, and
  // they fire on a person's click on Accept — never on a suggestion arriving.
  onAcceptDiagnosis,
  onAcceptProcedure,
}) {
  const enabled = !readOnly && features.note_viewer === true;

  const [notes, setNotes] = useState([]);
  const [review, setReview] = useState({
    reviewed: false,
    reviewedBy: null,
    reviewedAt: null,
  });
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);
  // The claim whose notes are on screen, once its load has settled. Distinct
  // from `loading`, which is false before the first load has even started.
  const [loadedFor, setLoadedFor] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [matchIndex, setMatchIndex] = useState(0);
  const [savingReview, setSavingReview] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [assistCost, setAssistCost] = useState(null);
  // The suggestion this claim already has, with what the biller decided about it.
  // Loaded with the notes rather than left for the panel to re-ask for, because
  // re-asking is the action that spends.
  const [suggestion, setSuggestion] = useState(null);
  // Which suggested line is selected, and the sentence it says it came from.
  const [picked, setPicked] = useState({ key: null, quote: null });

  const paneRef = useRef(null);
  const bodyRef = useRef(null);

  // Which load is current: a practice or claim switch re-renders this pane, and
  // the earlier claim's late answer must not replace the note on screen.
  const seq = useRef(0);
  useEffect(() => () => {
    seq.current += 1;
  }, []);

  const load = useCallback(async () => {
    if (!enabled || !practiceId || !claimId) return;
    const mine = (seq.current += 1);
    setLoading(true);
    setError(null);
    try {
      const body = await fetchClaimNotes(practiceId, claimId);
      if (mine !== seq.current) return;
      setNotes(body.notes);
      setReview(body.review);
      setAssistCost(body.assistCost);
      setSuggestion(body.suggestion);
      setActiveId(body.notes.length ? body.notes[0].id : null);
    } catch (err) {
      if (mine !== seq.current) return;
      setNotes([]);
      setError(err.message);
    } finally {
      if (mine === seq.current) {
        setLoading(false);
        setLoadedFor(claimId);
      }
    }
  }, [enabled, practiceId, claimId]);

  useEffect(() => {
    setQuery("");
    setMatchIndex(0);
    setNotice(null);
    setReviewError(null);
    setLoadedFor(null);
    setPicked({ key: null, quote: null });
    // CLEARED ON THE WAY IN, not left for the load to overwrite. `load()` runs from
    // an effect, and a `useEffect` runs AFTER the browser paints — so there is one
    // painted frame between the new claimId arriving and `loading` going true, and
    // SuggestPanel is keyed on claimId, so it mounts in that frame holding whatever
    // suggestion is still in state. That is the PREVIOUS claim's suggestion and the
    // previous biller's decisions, drawn under the new claim's id.
    //
    // NOT COVERED BY A TEST, deliberately, and this comment is the reason. `act()`
    // flushes effects before it returns, so the frame does not exist in the test
    // harness: a test written for it passes against the unfixed file too, which is
    // worse than no test. The fix is a line that cannot be wrong; the frame is real
    // in a browser and is not reachable from here.
    setSuggestion(null);
    load();
  }, [load]);

  const active = useMemo(
    () => notes.find((n) => n.id === activeId) || notes[0] || null,
    [notes, activeId],
  );

  const onActiveNoteChangeRef = useRef(onActiveNoteChange);
  onActiveNoteChangeRef.current = onActiveNoteChange;
  useEffect(() => {
    if (onActiveNoteChangeRef.current) onActiveNoteChangeRef.current(active);
  }, [active]);

  const body = active ? active.bodyText : "";
  const segments = useMemo(() => segmentNote(body), [body]);
  const matches = useMemo(() => findMatches(body, query), [body, query]);
  const shownQuote = picked.quote || quote;
  const quoteRange = useMemo(
    () => findQuote(body, shownQuote),
    [body, shownQuote],
  );

  const ranges = useMemo(() => {
    const out = [];
    if (quoteRange) out.push({ ...quoteRange, kind: "quote", active: true });
    matches.forEach((m, i) =>
      out.push({ ...m, kind: "match", active: i === matchIndex }),
    );
    return out;
  }, [quoteRange, matches, matchIndex]);

  // The plan of care's diagnosis list, for comparing against the claim's own.
  // Server-derived only: visit_note_service parses it on read, in one place.
  // This deliberately does NOT fall back to parsing the note here — a second
  // parser in the client is the two-passes failure CLAUDE.md #8 names, and the
  // two would drift the first time a 485 changed shape. Absent means no strip.
  const diagnoses = active ? active.diagnoses : [];

  // Keep the search cursor inside the result set as the note or query changes.
  useEffect(() => {
    setMatchIndex((i) => (matches.length === 0 ? 0 : Math.min(i, matches.length - 1)));
  }, [matches.length]);

  // Bring the current search hit into view. Found in the DOM rather than held in
  // a ref because the marks are built inside NoteBody as it slices the text.
  useEffect(() => {
    if (!matches.length || !bodyRef.current) return;
    scrollTo(bodyRef.current.querySelector('[data-mark-active="yes"]'));
  }, [matchIndex, matches.length, activeId]);

  useEffect(() => {
    if (quoteRange && bodyRef.current) {
      scrollTo(bodyRef.current.querySelector('[data-mark-kind="quote"]'));
    }
  }, [quoteRange]);

  // Arrived from the charges bucket's "Read the note": put the note on screen.
  // Taken once the load has settled whether or not there is a note to show —
  // a target left unconsumed would scroll some later, unrelated visit.
  useEffect(() => {
    if (loadedFor === claimId && takeNotesTarget(claimId)) {
      scrollTo(paneRef.current);
    }
  }, [loadedFor, claimId]);

  const step = (delta) => {
    if (!matches.length) return;
    setMatchIndex((i) => (i + delta + matches.length) % matches.length);
  };

  const jumpToAssessment = () => {
    const at = assessmentIndex(segments);
    if (at === -1) {
      setNotice("This note has no assessment section.");
      return;
    }
    setNotice(null);
    if (bodyRef.current) {
      scrollTo(bodyRef.current.querySelector(`[data-segment="${at}"]`));
    }
  };

  const copySelection = async () => {
    const selected = window.getSelection ? String(window.getSelection()) : "";
    if (!selected.trim()) {
      setNotice("Select some text in the note first, then copy.");
      return;
    }
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      setNotice("This browser will not let Atlas copy for you — use ⌘C.");
      return;
    }
    try {
      await navigator.clipboard.writeText(selected);
      setNotice("Copied the selection.");
    } catch (err) {
      setNotice("Could not copy — use ⌘C.");
    }
  };

  const toggleReviewed = async () => {
    const next = !review.reviewed;
    // Same guard the GET has: switching claim mid-save must not write this
    // claim's flag onto the pane now showing another one.
    const mine = seq.current;
    setSavingReview(true);
    setReviewError(null);
    setNotice(null);
    try {
      const saved = await setNoteReviewed(practiceId, claimId, next);
      if (mine !== seq.current) return;
      setReview(saved);
      if (onReviewedChange) onReviewedChange(saved);
      else
        setNotice(
          "Saved. The readiness checklist shows it the next time the claim loads.",
        );
    } catch (err) {
      if (mine !== seq.current) return;
      setReviewError(err.message);
    } finally {
      if (mine === seq.current) setSavingReview(false);
    }
  };

  if (!enabled) return null;

  return (
    <section className="ce-section nv-pane" aria-label="Visit note" ref={paneRef}>
      <h2>Visit note</h2>

      {loading && <div className="nv-status">Loading the note…</div>}
      {error && (
        <div className="form-message error" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && notes.length === 0 && (
        <p className="nv-status">
          No signed note has reached Atlas for this visit. Code it from the chart,
          or ask the provider to sign.
        </p>
      )}

      {notes.length > 1 && (
        <div className="nv-tabs" role="tablist" aria-label="Documents for this claim">
          {notes.map((note) => (
            <button
              key={note.id}
              type="button"
              role="tab"
              aria-selected={active && note.id === active.id}
              className={`nv-tab${active && note.id === active.id ? " active" : ""}`}
              onClick={() => {
                setActiveId(note.id);
                setMatchIndex(0);
                setNotice(null);
                setPicked({ key: null, quote: null });
              }}
            >
              {note.docLabel}
              {note.serviceDate && (
                <span className="nv-tab-date">{formatDate(note.serviceDate)}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {active && (
        <>
          <div className="nv-meta">
            <span className="nv-meta-title">{active.title || active.docLabel}</span>
            {active.author && <span>Signed by {active.author}</span>}
            {active.isSigned && active.signedAt ? (
              <span>{formatWhen(active.signedAt)}</span>
            ) : (
              // NOT "Not signed". The server derives signing from whether the
              // EMR sent a signature, so for every source that does not send one
              // this is the normal state, and calling it unsigned would assert
              // something about the provider that Atlas has no basis for.
              <span
                className="nv-unsigned"
                title="Atlas has no signature for this note from its EMR."
              >
                Signature not recorded
              </span>
            )}
            {active.serviceDate && (
              <span>Date of service {formatDate(active.serviceDate)}</span>
            )}
            {active.characters != null && (
              <span className="nv-source">
                {active.characters.toLocaleString()} characters
              </span>
            )}
            {active.sourceLabel && <span className="nv-source">{active.sourceLabel}</span>}
          </div>

          {diagnoses.length > 0 && (
            <div className="nv-dx" aria-label="Diagnoses on this document">
              {diagnoses.map((dx) => (
                <span key={`${dx.order}-${dx.code}`} className="nv-dx-chip">
                  <strong>{dx.order}.</strong> {dx.code}
                  {dx.description ? ` — ${dx.description}` : ""}
                </span>
              ))}
            </div>
          )}

          <div className="nv-toolbar">
            <label className="nv-search">
              <span className="nv-search-label">Search this note</span>
              <input
                type="search"
                value={query}
                placeholder="Find in note"
                onChange={(e) => {
                  setQuery(e.target.value);
                  setMatchIndex(0);
                }}
              />
            </label>
            <span className="nv-count" data-match-count>
              {query.trim()
                ? matches.length
                  ? `${matchIndex + 1} of ${matches.length}`
                  : "No matches"
                : ""}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => step(-1)}
              disabled={matches.length < 2}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => step(1)}
              disabled={matches.length < 2}
            >
              Next
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={jumpToAssessment}
            >
              Jump to Assessment
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={copySelection}
            >
              Copy selection
            </button>
          </div>

          {notice && <div className="nv-status">{notice}</div>}

          <div ref={bodyRef}>
            <NoteBody body={body} segments={segments} ranges={ranges} />
          </div>
        </>
      )}

      {!loading && !error && notes.length > 0 && (
        <SuggestPanel
          // KEYED ON THE CLAIM, NOT THE TAB. It used to include the open note's
          // id, which remounted the panel — and threw away the suggestion and
          // every decision made about it — the moment a biller switched tab.
          // The panel's own "coded from … not the document open above" message
          // invites exactly that switch, so following it destroyed the work, and
          // the next press returned the same cached row with the decisions gone.
          // The gate that key protected is gone too: the server chooses the note
          // and the panel names it, so a suggestion belongs to the claim.
          key={claimId}
          practiceId={practiceId}
          claimId={claimId}
          visitNote={active}
          notes={notes}
          features={features}
          canEdit={canEdit}
          claimIcds={claimIcds}
          claimLines={claimLines}
          cost={assistCost}
          initialSuggestion={suggestion}
          selectedKey={picked.key}
          onSelect={(key, pickedQuote) =>
            setPicked((prev) =>
              prev.key === key ? { key: null, quote: null } : { key, quote: pickedQuote },
            )
          }
          onAcceptDiagnosis={onAcceptDiagnosis}
          onAcceptProcedure={onAcceptProcedure}
        />
      )}

      {!loading && !error && notes.length > 0 && (
        <div className="nv-reviewed">
          <label>
            <input
              type="checkbox"
              checked={review.reviewed}
              disabled={savingReview}
              onChange={toggleReviewed}
            />{" "}
            Note reviewed
          </label>
          {review.reviewed && review.reviewedBy && (
            <span className="nv-reviewed-by">
              {review.reviewedBy}
              {review.reviewedAt ? ` · ${formatWhen(review.reviewedAt)}` : ""}
            </span>
          )}
          {savingReview && <span className="nv-reviewed-by">Saving…</span>}
        </div>
      )}
      {reviewError && (
        <div className="form-message error" role="alert">
          {reviewError}
        </div>
      )}
    </section>
  );
}
